<template>
  <aside class="admin-sidebar">
    <div class="sidebar-section">
      <div class="sidebar-label">Festival year</div>
      <select
        class="year-select"
        :value="adminFestivalYear"
        @change="onYearChange"
      >
        <option v-for="y in adminYears" :key="y" :value="y">
          {{ yearLabel(y) }}
        </option>
      </select>
      <p class="year-hint">
        <template v-if="isArchive">
          Archive — dancers, recordings, and competition results.
        </template>
        <template v-else>
          Full admin for this festival year. Dancer app stays on the live year.
        </template>
      </p>
    </div>

    <!-- Archive years: dancers + history editors -->
    <template v-if="isArchive">
      <div class="sidebar-section">
        <div class="sidebar-label">Archive</div>
        <router-link class="sidebar-link" to="/iabf/admin">Dancers</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/showcase-recordings">Showcase recordings</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/competition-results">Competition results</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/inventory">Festival inventory</router-link>
      </div>
      <div v-if="isFinanceAdmin" class="sidebar-section">
        <div class="sidebar-label">Finance</div>
        <router-link class="sidebar-link" to="/iabf/admin/accounting">Accounting</router-link>
      </div>
    </template>

    <!-- Operable years: full nav -->
    <template v-else>
      <div class="sidebar-section">
        <div class="sidebar-label">People</div>
        <router-link class="sidebar-link" to="/iabf/admin">Dancers</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/faculty">Faculty</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/canceled-dancers">Canceled dancers</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/guests">Meals &amp; guests</router-link>
      </div>
      <div v-if="isFinanceAdmin" class="sidebar-section">
        <div class="sidebar-label">Finance</div>
        <router-link class="sidebar-link" to="/iabf/admin/accounting">Accounting</router-link>
      </div>
      <div class="sidebar-section">
        <div class="sidebar-label">Showcase</div>
        <router-link class="sidebar-link" to="/iabf/admin/pieces">Showcase lineup</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/group-pieces">Group pieces</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/rehearsal-recordings">Rehearsal recordings</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/costume-orders">Costume orders</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/competition">Competition</router-link>
        <router-link
          v-if="showCorpsVideos"
          class="sidebar-link"
          to="/iabf/admin/choreographer-sheet"
        >
          Choreographer sheet
        </router-link>
      </div>
      <div class="sidebar-section">
        <div class="sidebar-label">During IABF</div>
        <router-link class="sidebar-link" to="/iabf/admin/itinerary">Itinerary</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/addon-services">Add-on services</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/photoshoots">Photoshoots</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/carpools">Carpools</router-link>
      </div>
      <div class="sidebar-section">
        <div class="sidebar-label">Festival prep</div>
        <router-link
          class="sidebar-link"
          :class="{ 'router-link-exact-active': isDeadlinesSection }"
          to="/iabf/admin/deadlines"
        >Deadlines &amp; access</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/store">Merch store</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/store/orders">Merch orders</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/inventory">Festival inventory</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/setup-plan">Setup plan</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/welcome-bag-packing">Welcome bag packing</router-link>
      </div>
      <!-- 2026-only corps pieces; 2027 will have different repertoire -->
      <div v-if="showCorpsVideos" class="sidebar-section">
        <div class="sidebar-label">Per-piece videos ({{ adminFestivalYear }})</div>
        <router-link class="sidebar-link" to="/iabf/admin/woh-videos">Waltz of the Hours</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/qoh-videos">Queen of Hearts</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/gw-videos">Garland Waltz</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/becoming-videos">Becoming</router-link>
        <router-link class="sidebar-link" to="/iabf/admin/jazz-videos">Le Jazz Hot</router-link>
      </div>
    </template>
  </aside>
</template>

<script>
import {
  adminYearLabel,
  hasCorpsVideoAdmin,
  isArchiveYear,
  IABF_ADMIN_YEARS
} from '@/config/iabf-event'

export default {
  name: 'AdminSidebar',
  computed: {
    isFinanceAdmin () {
      return this.$store.getters.isFinanceAdmin
    },
    adminFestivalYear () {
      return this.$store.getters.adminFestivalYear
    },
    adminYears () {
      return this.$store.getters.iabfAdminYears || IABF_ADMIN_YEARS
    },
    isArchive () {
      return isArchiveYear(this.adminFestivalYear)
    },
    showCorpsVideos () {
      return hasCorpsVideoAdmin(this.adminFestivalYear)
    },
    isDeadlinesSection () {
      const p = this.$route.path
      return p === '/iabf/admin/deadlines' || p === '/iabf/admin/hold-log'
    }
  },
  methods: {
    yearLabel (y) {
      return adminYearLabel(y)
    },
    onYearChange (e) {
      const year = parseInt(e.target.value, 10)
      this.$store.commit('setAdminFestivalYear', year)
      const path = this.$route.path
      // Routes that work for both archive and operable years — stay put.
      const sharedArchiveOk = [
        '/iabf/admin',
        '/iabf/admin/competition-results',
        '/iabf/admin/showcase-recordings',
        '/iabf/admin/accounting',
        '/iabf/admin/inventory'
      ]
      if (isArchiveYear(year) && !sharedArchiveOk.includes(path)) {
        this.$router.replace('/iabf/admin').then(() => {
          this.$router.go(0)
        }).catch(() => {
          this.$router.go(0)
        })
        return
      }
      this.$router.go(0)
    }
  }
}
</script>

<style scoped>
.admin-sidebar {
  position: sticky;
  top: 90px;
  align-self: start;
  background: #fbfaf6;
  border-right: 1px solid #eee5d2;
  padding: 18px 14px;
  font-family: 'Fanwood Text', Georgia, serif;
  max-height: calc(100vh - 90px);
  overflow-y: auto;
  box-sizing: border-box;
}

.sidebar-section {
  margin-bottom: 16px;
}

.sidebar-section:last-child {
  margin-bottom: 0;
}

.sidebar-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: #999;
  font-weight: 700;
  margin-bottom: 6px;
  padding: 0 6px;
}

.year-select {
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  border: 1px solid #eee5d2;
  border-radius: 4px;
  background: #fff;
  font-family: inherit;
  font-size: 14px;
  color: #1a1a1a;
  margin-bottom: 4px;
}

.year-hint {
  font-size: 11px;
  color: #999;
  line-height: 1.35;
  margin: 0 0 4px;
  padding: 0 2px;
}

.sidebar-link {
  display: block;
  padding: 6px 10px;
  border-radius: 4px;
  color: #1a1a1a;
  text-decoration: none;
  font-size: 14px;
  margin-bottom: 1px;
  transition: background .12s;
}

.sidebar-link:hover {
  background: #f1ebd9;
}

.sidebar-link.router-link-exact-active {
  background: #388b9d;
  color: #fff;
}

.sidebar-link.is-primary {
  color: #267E7D;
  font-weight: 700;
}

.sidebar-link.is-primary:hover {
  background: #def0ef;
}

@media (max-width: 800px) {
  .admin-sidebar {
    position: static;
    min-height: 0;
    border-right: none;
    border-bottom: 1px solid #eee5d2;
  }
}
</style>
